<template>
  <div class="container-fluid mt-4 oura-trends">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div>
        <h2 class="mb-0"><i class="bi bi-activity me-2"></i>Oura</h2>
        <small class="text-muted">Tendencias inspiradas en Oura Cloud · datos vía API oficial</small>
        <div v-if="status?.connected && ultimaActualizacionUi" class="small text-muted mt-1">
          Última actualización: {{ ultimaActualizacionUi }}
        </div>
      </div>
      <div class="d-flex gap-2 flex-wrap">
        <button
          v-if="!status?.connected"
          type="button"
          class="btn btn-primary"
          :disabled="conectando || !oauthDisponible"
          @click="conectarOura"
        >
          <span v-if="conectando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-link-45deg me-1"></i>
          Conectar Oura
        </button>
        <template v-else>
          <router-link to="/analisis-oura" class="btn btn-primary">
            <i class="bi bi-circle-half me-1"></i>
            Oura Lab
          </router-link>
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="sincronizando"
            @click="sincronizarRapido(14)"
          >
            <span v-if="sincronizando && syncMode === '14'" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrow-repeat me-1"></i>
            Sincronizar 14 días
          </button>
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="sincronizando"
            @click="sincronizarRapido(30)"
          >
            <span v-if="sincronizando && syncMode === '30'" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrow-repeat me-1"></i>
            30 días
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="sincronizando"
            @click="importarHistorico"
          >
            <span v-if="sincronizando && syncMode === 'hist'" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-cloud-download me-1"></i>
            Importar histórico (~2 años)
          </button>
          <button type="button" class="btn btn-outline-danger" :disabled="sincronizando" @click="desconectar">
            <i class="bi bi-x-circle me-1"></i>
            Desconectar
          </button>
        </template>
      </div>
    </div>

    <div v-if="mensajeOAuth" class="alert" :class="mensajeOAuthOk ? 'alert-success' : 'alert-danger'">
      {{ mensajeOAuth }}
    </div>

    <div v-if="!oauthDisponible" class="alert alert-warning">
      El servidor no tiene configuradas las variables <code>OURA_CLIENT_ID</code> y <code>OURA_CLIENT_SECRET</code>.
      Registra una aplicación en
      <a href="https://developer.ouraring.com/applications" target="_blank" rel="noopener">developer.ouraring.com</a>
      y configura la <strong>Redirect URI</strong> igual que en el backend.
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-if="status?.needsReauth || (!status?.connected && status?.lastAutoSyncError)" class="alert alert-warning">
      <strong>Hay que volver a conectar Oura.</strong>
      {{ status.lastAutoSyncError || 'El token OAuth caducó o fue revocado; la sync automática no puede renovarlo.' }}
      <div class="mt-2">
        <button type="button" class="btn btn-sm btn-primary" :disabled="conectando || !oauthDisponible" @click="conectarOura">
          Reconectar Oura
        </button>
      </div>
    </div>

    <div
      v-else-if="status?.connected && status?.lastAutoSyncError"
      class="alert alert-warning"
    >
      <strong>La sync automática está fallando</strong>
      <span v-if="status.lastAutoSyncErrorAt"> (desde {{ status.lastAutoSyncErrorAt }})</span>:
      {{ status.lastAutoSyncError }}
      <span v-if="status.tokenExpired || !status.hasRefreshToken">
        Prueba «Sincronizar» o reconecta Oura si el error continúa.
      </span>
    </div>

    <div v-if="status?.connected" class="card border-0 shadow-sm mb-3 bg-light">
      <div class="card-body py-2 d-flex flex-wrap gap-3 small text-muted">
        <span>Token: {{ status.updatedAt || '—' }}</span>
        <span v-if="status.lastSyncAt">Última sync manual: {{ status.lastSyncAt }}</span>
        <span v-if="status.lastAutoSyncAt">Sync automática: {{ status.lastAutoSyncAt }}</span>
        <span v-if="status.tokenExpired" class="text-danger">Access token caducado</span>
        <span v-if="status.connected && !status.hasRefreshToken" class="text-danger">Sin refresh token</span>
      </div>
    </div>

    <div v-if="status?.connected && ultimaSync" class="alert alert-light border small mb-3 py-2">
      Última operación de sync: {{ ultimaSync.desde }} → {{ ultimaSync.hasta }}
      <span v-if="ultimaSync.counts">({{ resumenCounts(ultimaSync.counts) }})</span>
    </div>

    <!-- Tarjetas: último valor disponible por métrica (hoy puede no tener score de actividad aún) -->
    <div v-if="status?.connected && trends?.latest" class="row g-3 mb-4">
      <div class="col-md-4">
        <div class="card h-100 border-0 shadow-sm oura-card oura-card--readiness">
          <div class="card-body text-center py-4">
            <div class="display-4 fw-bold text-oura-navy">{{ fmtScore(trends.latest.readiness) }}</div>
            <div class="text-uppercase small fw-semibold text-muted mt-1">Readiness</div>
            <div class="small text-muted oura-card-help mt-1">Preparación y recuperación del cuerpo</div>
            <div v-if="trends.latest.readinessDia" class="small text-muted mt-2">{{ formatDiaEtiqueta(trends.latest.readinessDia) }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card h-100 border-0 shadow-sm oura-card oura-card--sleep">
          <div class="card-body text-center py-4">
            <div class="display-4 fw-bold text-oura-blue">{{ fmtScore(trends.latest.sleep) }}</div>
            <div class="text-uppercase small fw-semibold text-muted mt-1">Sleep</div>
            <div class="small text-muted oura-card-help mt-1">Calidad del sueño (score 0–100)</div>
            <div v-if="trends.latest.sleepMinutes != null" class="small text-muted mt-2">
              Total sueño: {{ formatSleepMin(trends.latest.sleepMinutes) }}
            </div>
            <div v-if="trends.latest.sleepDia" class="small text-muted mt-1">{{ formatDiaEtiqueta(trends.latest.sleepDia) }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card h-100 border-0 shadow-sm oura-card oura-card--activity">
          <div class="card-body text-center py-4">
            <div class="display-4 fw-bold text-oura-orange">
              {{ trends.latest.activityScorePending ? '—' : fmtScore(trends.latest.activity) }}
            </div>
            <div class="text-uppercase small fw-semibold text-muted mt-1">Activity</div>
            <div class="small text-muted oura-card-help mt-1">Actividad diaria (movimiento y carga)</div>
            <div v-if="trends.latest.activityScorePending" class="small text-muted mt-1">Score del día aún pendiente</div>
            <div v-if="trends.latest.steps != null" class="small text-muted mt-2">
              {{ Math.round(trends.latest.steps) }} pasos
              <span v-if="trends.latest.activeCalories != null"> · {{ Math.round(trends.latest.activeCalories) }} kcal activas</span>
            </div>
            <div v-if="trends.latest.activityDia" class="small text-muted mt-1">{{ formatDiaEtiqueta(trends.latest.activityDia) }}</div>
            <div
              v-if="trends.latest.activityLastScore != null"
              class="small text-muted mt-2 border-top pt-2"
            >
              Último score cerrado: {{ fmtScore(trends.latest.activityLastScore) }}
              <span v-if="trends.latest.activityLastScoreDia">
                ({{ formatDiaEtiqueta(trends.latest.activityLastScoreDia) }})
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <p v-if="status?.connected" class="small text-muted mb-3">
      El score de <strong>actividad</strong> del día en curso a menudo llega más tarde (Oura lo cierra al final del día).
      Mientras tanto se muestran los <strong>pasos y kcal de hoy</strong>; el último score cerrado queda como referencia.
    </p>

    <!-- Gráfica tendencias -->
    <div v-if="status?.connected" class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-bottom-0 py-3">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <h5 class="mb-0"><i class="bi bi-graph-up-arrow me-2"></i>Tendencias</h5>
          <div class="d-flex flex-wrap align-items-center gap-2">
            <label class="small text-muted mb-0 me-1">Periodo</label>
            <select
              v-model="trendsGroupBy"
              class="form-select form-select-sm"
              style="width: auto; min-width: 9rem"
              :disabled="cargandoTrends"
              @change="cargarTrends"
            >
              <option v-for="g in agrupacionesTrends" :key="g.id" :value="g.id">{{ g.label }}</option>
            </select>
          </div>
        </div>
        <div class="btn-group btn-group-sm flex-wrap" role="group">
          <button
            v-for="w in ventanasGrafica"
            :key="w.d"
            type="button"
            class="btn"
            :class="trendsDays === w.d ? 'btn-primary' : 'btn-outline-primary'"
            :disabled="cargandoTrends"
            @click="cambiarVentanaTrends(w.d)"
          >
            {{ w.label }}
          </button>
        </div>
      </div>
      <div class="card-body">
        <div v-if="cargandoTrends" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
        </div>
        <div v-else-if="trends && trends.labels?.length" class="oura-chart-wrap">
          <Line :data="chartData" :options="chartOptions" class="chart-container" />
        </div>
        <p v-else class="text-muted mb-0 small">
          No hay datos para la gráfica. Pulsa «Sincronizar» o «Importar histórico».
        </p>
        <p class="text-muted small mt-3 mb-0">
          Las tres líneas son puntuaciones 0–100. Con <strong>Semana</strong> o <strong>Mes</strong> se muestran
          <strong>medias</strong> de cada periodo (como en Oura Cloud). La sincronización automática en servidor corre varias
          veces al día (por defecto 8:00, 14:00 y 21:00 hora Madrid).
        </p>
      </div>
    </div>

    <ul v-if="status?.connected" class="nav nav-tabs mb-3">
      <li v-for="t in tipos" :key="t.id" class="nav-item">
        <button
          type="button"
          class="nav-link"
          :class="{ active: tab === t.id }"
          @click="cambiarTab(t.id)"
        >
          {{ t.label }}
        </button>
      </li>
    </ul>

    <div v-if="cargandoDatos" class="text-center py-4">
      <div class="spinner-border text-primary"></div>
    </div>

    <div v-else-if="status?.connected && filas.length" class="table-responsive">
      <table class="table table-sm table-hover align-middle">
        <thead>
          <tr>
            <th>Día</th>
            <th v-if="tab === 'daily_sleep'">Score sueño</th>
            <th v-if="tab === 'daily_readiness'">Readiness</th>
            <th
              v-if="tab === 'daily_sleep'"
              class="text-nowrap"
              title="Media de VFC durante el sueño (ms), misma fuente que «Average HRV» en Oura. No confundir con «hrv_balance» del readiness (0–100)."
            >
              VFC (ms)
            </th>
            <th v-if="tab === 'daily_activity'">Pasos</th>
            <th v-if="tab === 'workout'">Actividad</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in filas" :key="row.oura_id">
            <td>{{ row.dia || '—' }}</td>
            <td v-if="tab === 'daily_sleep'">{{ row.item?.score ?? '—' }}</td>
            <td v-if="tab === 'daily_sleep'">{{ fmtHrvMsOnly(row) }}</td>
            <td v-if="tab === 'daily_readiness'">{{ row.item?.score ?? '—' }}</td>
            <td v-if="tab === 'daily_activity'">{{ row.item?.steps ?? row.item?.equivalent_walking_distance ?? '—' }}</td>
            <td v-if="tab === 'workout'">{{ row.item?.sport ?? row.item?.activity ?? '—' }}</td>
            <td>
              <button type="button" class="btn btn-link btn-sm p-0" @click="detalle = row.item">Ver JSON</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="status?.connected && !cargandoDatos" class="text-muted">
      No hay datos para esta pestaña. Pulsa «Sincronizar».
    </div>

    <div v-if="!status?.connected && !conectando" class="card border-0 bg-light">
      <div class="card-body">
        <h5 class="card-title">Antes de conectar</h5>
        <ol class="mb-0 small">
          <li>
            Crea una app en
            <a href="https://cloud.ouraring.com/oauth/applications" target="_blank" rel="noopener">My Applications (Oura)</a>
            y copia Client ID y Secret al servidor (<code>.env.oura</code>). Tras editar, recrea el contenedor backend (no solo restart).
          </li>
          <li>
            En la app Oura, añade la <strong>Redirect URI</strong> <em>exactamente</em> igual que la del servidor.
            Si Oura muestra «Ha habido un error» al iniciar sesión, casi siempre es que esta URI no coincide con la lista blanca de la aplicación.
          </li>
          <li>En la app Oura del móvil, tus datos deben existir; esta pantalla solo lee lo que expone la API.</li>
        </ol>
        <p v-if="oauthConfig?.redirectUri" class="mb-0 mt-2 small">
          <strong>URI que usa este backend:</strong>
          <code class="user-select-all">{{ oauthConfig.redirectUri }}</code>
        </p>
        <p v-if="oauthConfig?.scopes" class="mb-0 mt-1 text-muted small">Scopes solicitados: {{ oauthConfig.scopes }}</p>
      </div>
    </div>

    <div
      v-if="detalle"
      class="modal d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,0.4)"
      @click.self="detalle = null"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Detalle Oura</h5>
            <button type="button" class="btn-close" aria-label="Cerrar" @click="detalle = null"></button>
          </div>
          <div class="modal-body">
            <pre class="small mb-0" style="max-height: 70vh; overflow: auto">{{ JSON.stringify(detalle, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '../config/axios';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LineController,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, Title, Tooltip, Legend, LineElement, PointElement, LineController, Filler);

const COL_READINESS = '#1e3a5f';
const COL_SLEEP = '#5b9bd5';
const COL_ACTIVITY = '#ed7d31';

export default {
  name: 'SaludOura',
  components: { Line },
  data() {
    return {
      status: null,
      oauthDisponible: true,
      conectando: false,
      sincronizando: false,
      syncMode: '',
      cargandoDatos: false,
      cargandoTrends: false,
      error: null,
      tab: 'daily_sleep',
      datos: [],
      ultimaSync: null,
      detalle: null,
      mensajeOAuth: '',
      mensajeOAuthOk: true,
      oauthConfig: null,
      trends: null,
      trendsDays: 365,
      trendsGroupBy: 'week',
      agrupacionesTrends: [
        { id: 'day', label: 'Diario' },
        { id: 'week', label: 'Semana (media)' },
        { id: 'month', label: 'Mes (media)' },
      ],
      ventanasGrafica: [
        { d: 14, label: '14 d' },
        { d: 30, label: '30 d' },
        { d: 90, label: '90 d' },
        { d: 180, label: '180 d' },
        { d: 365, label: '365 d' },
        { d: 720, label: '720 d' },
      ],
      tipos: [
        { id: 'daily_sleep', label: 'Sueño' },
        { id: 'daily_readiness', label: 'Readiness' },
        { id: 'daily_activity', label: 'Actividad diaria' },
        { id: 'workout', label: 'Entrenos' },
      ],
    };
  },
  computed: {
    ultimaActualizacionUi() {
      if (!this.status?.connected) return '';
      // Preferimos la última sync (auto/manual) frente a la actualización del token.
      return this.status.lastAutoSyncAt || this.status.lastSyncAt || this.status.updatedAt || '';
    },
    filas() {
      return this.datos;
    },
    chartData() {
      const t = this.trends;
      if (!t?.labels?.length) {
        return { labels: [], datasets: [] };
      }
      const labels = t.labels.map((raw) => this.formatChartAxisLabel(raw));
      return {
        labels,
        datasets: [
          {
            label: 'Readiness',
            data: t.series.readiness,
            borderColor: COL_READINESS,
            backgroundColor: 'transparent',
            tension: 0.25,
            spanGaps: true,
            pointRadius: 2,
            pointHoverRadius: 5,
          },
          {
            label: 'Sleep',
            data: t.series.sleepScore,
            borderColor: COL_SLEEP,
            backgroundColor: 'transparent',
            tension: 0.25,
            spanGaps: true,
            pointRadius: 2,
            pointHoverRadius: 5,
          },
          {
            label: 'Activity',
            data: t.series.activityScore,
            borderColor: COL_ACTIVITY,
            backgroundColor: 'transparent',
            tension: 0.25,
            spanGaps: true,
            pointRadius: 2,
            pointHoverRadius: 5,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true, boxWidth: 8 },
          },
          tooltip: {
            callbacks: {
              footer: (items) => {
                if (!items.length || !this.trends) return '';
                const idx = items[0].dataIndex;
                const g = this.trends.groupBy || 'day';
                const sm = this.trends.series.sleepMinutes?.[idx];
                const st = this.trends.series.steps?.[idx];
                const kcal = this.trends.series.activeCalories?.[idx];
                const parts = [];
                if (sm != null) {
                  parts.push(
                    g === 'day'
                      ? `Sueño: ${this.formatSleepMin(sm)}`
                      : `Sueño (min. media): ${Math.round(sm)}`
                  );
                }
                if (st != null) {
                  parts.push(g === 'day' ? `Pasos: ${Math.round(st)}` : `Pasos (media): ${Math.round(st)}`);
                }
                if (kcal != null) {
                  parts.push(
                    g === 'day'
                      ? `Kcal activas: ${Math.round(kcal)}`
                      : `Kcal activas (media): ${Math.round(kcal)}`
                  );
                }
                if (!parts.length) return '';
                return (g !== 'day' ? 'Media del periodo · ' : '') + parts.join(' · ');
              },
            },
          },
        },
        scales: {
          y: {
            min: 0,
            max: 100,
            grid: { color: 'rgba(0,0,0,0.06)' },
            title: { display: true, text: 'Puntuación (0–100)' },
          },
          x: {
            grid: { display: false },
            ticks: {
              maxRotation: 45,
              minRotation: 0,
              autoSkip: true,
              maxTicksLimit: this.trends?.groupBy === 'day' ? 24 : 36,
            },
          },
        },
      };
    },
  },
  async mounted() {
    this.leerQueryOAuth();
    await this.cargarStatus();
    await this.cargarOAuthConfig();
    if (this.status?.connected) {
      await Promise.all([this.cargarTrends(), this.cargarDatos()]);
      // Si falta la actividad de hoy (Oura la publica a lo largo del día), pide 2 días al vuelo.
      await this.refrescarActividadSiFaltaHoy();
    }
  },
  methods: {
    fmtScore(v) {
      if (v == null || v === '') return '—';
      return Math.round(Number(v));
    },
    fmtHrvMsOnly(row) {
      const ms = row?.average_hrv_ms;
      if (ms == null || ms === '') return '—';
      return `${Math.round(Number(ms))} ms`;
    },
    formatSleepMin(min) {
      if (min == null) return '';
      const h = Math.floor(min / 60);
      const m = min % 60;
      return `${h} h ${m} min`;
    },
    formatDiaEtiqueta(iso) {
      if (!iso) return '';
      const [y, m, d] = iso.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
    },
    formatChartAxisLabel(raw) {
      const g = this.trends?.groupBy || 'day';
      if (!raw) return '';
      if (g === 'month') {
        const parts = raw.split('-');
        if (parts.length >= 2) {
          const y = parseInt(parts[0], 10);
          const mo = parseInt(parts[1], 10);
          return new Date(y, mo - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
        }
      }
      const [y, m, day] = raw.split('-').map((x) => parseInt(x, 10));
      if (!Number.isFinite(y) || !Number.isFinite(m)) return raw;
      const date = new Date(y, m - 1, day || 1);
      if (g === 'week') {
        return `Sem. ${date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}`;
      }
      return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    },
    leerQueryOAuth() {
      const q = this.$route.query;
      if (q.oura === 'ok') {
        this.mensajeOAuth = 'Oura conectado correctamente.';
        this.mensajeOAuthOk = true;
        this.$router.replace({ path: '/salud-oura' });
      } else if (q.oura === 'error') {
        this.mensajeOAuth = `Error al conectar Oura: ${q.message || 'desconocido'}`;
        this.mensajeOAuthOk = false;
        this.$router.replace({ path: '/salud-oura' });
      }
    },
    resumenCounts(c) {
      if (!c || typeof c !== 'object') return '';
      return Object.entries(c)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
    },
    async cargarStatus() {
      this.error = null;
      try {
        const { data } = await axios.get('/api/oura/status');
        this.status = data;
        this.oauthDisponible = true;
      } catch (e) {
        if (e.response?.status === 503) {
          this.oauthDisponible = false;
          this.status = { connected: false };
        } else {
          this.error = e.response?.data?.error || e.message || 'Error al cargar estado';
        }
      }
    },
    async cargarOAuthConfig() {
      try {
        const { data } = await axios.get('/api/oura/oauth-config');
        this.oauthConfig = data;
      } catch {
        this.oauthConfig = null;
      }
    },
    async cargarTrends() {
      if (!this.status?.connected) return;
      this.cargandoTrends = true;
      try {
        const { data } = await axios.get('/api/oura/trends', {
          params: { days: this.trendsDays, groupBy: this.trendsGroupBy },
        });
        this.trends = data;
      } catch {
        this.trends = null;
      } finally {
        this.cargandoTrends = false;
      }
    },
    async refrescarActividadSiFaltaHoy() {
      const t = this.trends;
      if (!t?.latest || !t.to) return;
      const hoy = t.to;
      const diaAct = t.latest.activityDia;
      // Ya tenemos datos de actividad de hoy (con o sin score): no hace falta.
      if (diaAct === hoy) return;
      try {
        await axios.post('/api/oura/sync', { days: 2 }, { timeout: 120000 });
        await Promise.all([this.cargarStatus(), this.cargarTrends()]);
      } catch {
        /* sync en segundo plano: si falla, se mantiene lo ya cargado */
      }
    },
    async cambiarVentanaTrends(d) {
      this.trendsDays = d;
      await this.cargarTrends();
    },
    async conectarOura() {
      this.conectando = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/oura/oauth-url');
        if (data.url) {
          window.location.href = data.url;
        } else {
          this.error = 'No se recibió URL de autorización';
        }
      } catch (e) {
        if (e.response?.status === 503) {
          this.oauthDisponible = false;
        }
        this.error = e.response?.data?.error || e.message || 'Error';
      } finally {
        this.conectando = false;
      }
    },
    async sincronizarRapido(days) {
      this.sincronizando = true;
      this.syncMode = String(days);
      this.error = null;
      try {
        const { data } = await axios.post('/api/oura/sync', { days }, { timeout: 180000 });
        this.ultimaSync = data;
        await Promise.all([this.cargarStatus(), this.cargarTrends(), this.cargarDatos()]);
      } catch (e) {
        this.error = e.response?.data?.error || e.message || 'Error al sincronizar';
        if (e.response?.data?.needsReauth) {
          await this.cargarStatus();
        }
      } finally {
        this.sincronizando = false;
        this.syncMode = '';
      }
    },
    async importarHistorico() {
      if (
        !window.confirm(
          'Se importarán hasta ~2 años de datos (varias peticiones a Oura). Puede tardar varios minutos. ¿Continuar?'
        )
      ) {
        return;
      }
      this.sincronizando = true;
      this.syncMode = 'hist';
      this.error = null;
      try {
        const { data } = await axios.post('/api/oura/sync', { days: 730 }, { timeout: 600000 });
        this.ultimaSync = data;
        await Promise.all([this.cargarStatus(), this.cargarTrends(), this.cargarDatos()]);
      } catch (e) {
        this.error = e.response?.data?.error || e.message || 'Error al importar histórico';
      } finally {
        this.sincronizando = false;
        this.syncMode = '';
      }
    },
    async desconectar() {
      if (!window.confirm('¿Desconectar Oura y borrar datos sincronizados en esta app?')) return;
      this.error = null;
      try {
        await axios.delete('/api/oura/disconnect');
        this.status = { connected: false };
        this.datos = [];
        this.trends = null;
        this.ultimaSync = null;
      } catch (e) {
        this.error = e.response?.data?.error || e.message || 'Error';
      }
    },
    async cambiarTab(id) {
      this.tab = id;
      await this.cargarDatos();
    },
    async cargarDatos() {
      this.cargandoDatos = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/oura/datos', {
          params: { tipo: this.tab, limit: 120 },
        });
        this.datos = data.data || [];
      } catch (e) {
        this.error = e.response?.data?.error || e.message || 'Error al cargar datos';
        this.datos = [];
      } finally {
        this.cargandoDatos = false;
      }
    },
  },
};
</script>

<style scoped>
.oura-trends :deep(.text-muted) {
  color: #6c757d !important;
}

.text-oura-navy {
  color: #1e3a5f;
}
.text-oura-blue {
  color: #4472c4;
}
.text-oura-orange {
  color: #ed7d31;
}
.oura-card {
  border-radius: 12px;
}
.oura-card-help {
  line-height: 1.2;
}
.oura-chart-wrap {
  position: relative;
  height: 320px;
}
.chart-container {
  width: 100%;
  height: 100%;
}
</style>
